import { ReactNode } from 'react'
import { useIntl } from 'react-intl'
import ChangeLine from '../../../components/novarium/ChangeLine'
import InfoTip from '../../../components/novarium/InfoTip'
import { formatShortDate, periodNeedsYear } from '../../../components/novarium/periodRange'
import { UrgencySummaryDTO } from '../../../dto/urgencySummary/UrgencySummaryDTO'
import { percentVariation, pointsVariation, Variation, Verdict, waitVerdict } from '../../ServicesStatistics/statisticsChange'
import { parseIsoDate } from '../../ServicesStatistics/statisticsUtils'
import { formatCount, formatMinutes, formatPercent } from '../urgencySummaryFormat'
import UrgencyInProgressNote from './UrgencyInProgressNote'

interface UrgencyIndicatorsProps {
  summary: UrgencySummaryDTO
}

interface IndicatorProps {
  label: string
  value: string
  /** Frase corta que explica el indicador (ⓘ). */
  hint?: string
  /** Renglones de detalle: la cantidad detrás del porcentaje, la variación contra el período anterior. */
  details?: ReactNode[]
}

function Indicator({ label, value, hint, details = [] }: IndicatorProps) {
  return (
    <div className="nv-card nv-urg-ind">
      <dt className="nv-urg-ind__label">
        {label}
        {hint && <InfoTip text={hint} />}
      </dt>
      <dd className="nv-kpi">{value}</dd>
      {details.length === 0 && <dd className="nv-urg-ind__detail">{' '}</dd>}
      {details.map((detail, index) => <dd key={index} className="nv-urg-ind__detail">{detail}</dd>)}
    </div>
  )
}

/** Un porcentaje (en %) como fracción, que es lo que compara `pointsVariation`. */
const toRatio = (percent: number | null): number | null => (percent === null ? null : percent / 100)

/** "26/8–24/9": el período anterior con que se compara. */
function previousPeriodText(summary: UrgencySummaryDTO): string {
  const from = parseIsoDate(summary.previousFrom)
  const to = parseIsoDate(summary.previousTo)
  const withYear = periodNeedsYear(from, to, new Date())
  return `${formatShortDate(from, withYear)}–${formatShortDate(to, withYear)}`
}

/**
 * Los cuatro indicadores del período, cada uno con su variación contra el período anterior de igual largo, escrita con las
 * fechas de ese período y, donde bajar es mejor, con veredicto (mejor o peor): llegadas (neutro, sin veredicto), espera
 * hasta ser atendido, cuántos esperaron más de 1 hora y cuántos se retiraron sin atención. Los registros duplicados y los
 * pacientes en curso no entran en los indicadores y se informan aparte.
 */
export default function UrgencyIndicators({ summary }: UrgencyIndicatorsProps) {
  const intl = useIntl()
  const { current, previous } = summary
  const period = previousPeriodText(summary)

  const line = (variation: Variation | null, verdict?: Verdict): ReactNode =>
    (variation ? <ChangeLine variation={variation} period={period} verdict={verdict} /> : undefined)
  /** Bajar es mejor: el veredicto sale de la dirección del cambio. */
  const lowerIsBetter = (variation: Variation | null): ReactNode =>
    line(variation, variation ? waitVerdict(variation.trend) : null)

  const arrivals = percentVariation(current.arrivals, previous.arrivals)
  const wait = current.avgWaitMinutes === null ? null : percentVariation(current.avgWaitMinutes, previous.avgWaitMinutes)
  const over60 = pointsVariation(intl, toRatio(current.waitOver60Pct), toRatio(previous.waitOver60Pct))
  const left = pointsVariation(intl, toRatio(current.leftWithoutAttentionPct), toRatio(previous.leftWithoutAttentionPct))

  const patients = (count: number): string =>
    intl.formatMessage({ id: 'UrgencySummaryPatients' }, { count, countText: formatCount(intl, count) })

  return (
    <div className="nv-urg-indicators-box">
      <dl className="nv-urg-indicators" aria-label={intl.formatMessage({ id: 'UrgencySummaryKpisLabel' })}>
        <Indicator
          label={intl.formatMessage({ id: 'UrgencySummaryKpiArrivals' })}
          hint={intl.formatMessage({ id: 'UrgencySummaryKpiArrivalsHint' })}
          value={formatCount(intl, current.arrivals)}
          details={[line(arrivals)]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'UrgencySummaryKpiWait' })}
          hint={intl.formatMessage({ id: 'UrgencySummaryKpiWaitHint' })}
          value={formatMinutes(intl, current.avgWaitMinutes)}
          details={[lowerIsBetter(wait)]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'UrgencySummaryKpiWait60' })}
          hint={intl.formatMessage({ id: 'UrgencySummaryKpiWait60Hint' })}
          value={formatPercent(intl, current.waitOver60Pct)}
          details={[
            current.waitedCount > 0 ? patients(current.waitOver60) : undefined,
            lowerIsBetter(over60),
          ]}
        />
        <Indicator
          label={intl.formatMessage({ id: 'UrgencySummaryKpiLeft' })}
          hint={intl.formatMessage({ id: 'UrgencySummaryKpiLeftHint' })}
          value={formatPercent(intl, current.leftWithoutAttentionPct)}
          details={[
            current.arrivals > 0 ? patients(current.leftWithoutAttention) : undefined,
            lowerIsBetter(left),
            current.duplicates > 0 ? (
              <span className="nv-urg-ind__note">
                {intl.formatMessage(
                  { id: 'UrgencySummaryKpiDuplicates' },
                  { count: current.duplicates, countText: formatCount(intl, current.duplicates) },
                )}
                <InfoTip text={intl.formatMessage({ id: 'UrgencySummaryKpiDuplicatesHint' })} />
              </span>
            ) : undefined,
          ]}
        />
      </dl>
      <UrgencyInProgressNote count={current.inProgress} />
    </div>
  )
}
